import { useEffect, useMemo, useState } from "react";

import { A4_HEIGHT_72DPI } from "../constants";
import parseDocXFile from "../utils/parseDocXFile";
import useDocumentVirtualizer from "./useDocumentVirtualizer";

const useDocxDocument = (file, ref, { onLoadSuccess } = {}) => {
  const [nodes, setNodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const hasSinglePage = nodes.length <= 1;

  const processDocument = async () => {
    const result = await parseDocXFile(file);
    const resultNodes = result.nodes || [];
    const pageIntros = resultNodes.map((el) => el.textContent);

    setNodes(resultNodes || []);
    onLoadSuccess({
      baseWidth: result.baseWidth,
      totalPages: resultNodes.length,
      pageIntros,
      paragraphRows: result.paragraphRows || {},
      documentParagraphs: result.documentParagraphs || [],
    });
    setIsLoading(false);
  };

  useEffect(() => {
    setIsLoading(true);
    setNodes([]);

    if (!file) {
      setIsLoading(false);
      return;
    }

    processDocument();
  }, [file]);

  const nodesData = useMemo(() => {
    if (!nodes.length) {
      return [];
    }

    // If there are definitely multiple "pages" in the DOCX, just
    // return them as-is, and treat each page as the virtualized origin.
    if (nodes.length > 1) {
      return nodes;
    }

    // Otherwise, pull out the first/only Page, and the single page's child nodes,
    // using those as the virtualized data to use in the UI...
    const [section] = nodes;
    const [article] = section.getElementsByTagName("article");

    return Array.from(article.childNodes);
  }, [nodes.length]);

  const { scrollToCenter, virtualizer } = useDocumentVirtualizer({
    count: nodesData.length,
    estimateSize: () => (hasSinglePage ? 70 : A4_HEIGHT_72DPI),
    overscan: hasSinglePage ? 15 : 5,
    scrollRef: ref,
  });

  return {
    hasSinglePage,
    isLoading,
    nodes: nodesData,
    scrollToCenter,
    virtualizer,
  };
};

export default useDocxDocument;
